import { Form, Row } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { IAmlz, ISubj, ISubjPrilozenesoubory } from 'types/amlz';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputTextArea from 'components/atoms/InputTextArea';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import TableComponent from 'components/atoms/TableComponent';
import Title from 'components/atoms/Title';
import UploadComponent, { IFileList } from 'components/atoms/UploadComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';

import { IcisStaty } from 'services/cisStaty';
import fileDownload from 'services/fileDownload';
import { prepareFileAmlz } from 'services/prepareFile';
import { EEsmStav } from 'services/spravaEmitenta/C4osoby';
import C6PdfApi, { C6BisignApi } from 'services/spravaEmitenta/C6Pdf';
import { amlzC6Send } from 'services/spravaEmitenta/amlzData';
import { EStavZadostAmlz } from 'services/spravaEmitenta/historickeZadosti';
import { EZadostPravoIdentifikace } from 'services/spravaEmitenta/zadostPravo';

import { EAmlzFormularSteps } from 'enums/amlz/amlzFormularSteps';
import { EAnoNe } from 'enums/anoNe';
import routePathEnum from 'enums/routePathEnum';

import c5PepSelect from 'data/codebooks/amlz/c5jePepSelect';
import anoNeSelect from 'data/codebooks/anoNeSelect';

interface IC6 {
    formData: IAmlz['amlz'];
    countries?: IcisStaty[];
    prevStep: () => void;
    setCurrentStep: (v: number) => void;
    setIsC6edit: (v: EAmlzFormularSteps | '') => void;
    idZadosti: number;
    isLegalEntity: boolean;
}

const C6: React.FC<IC6> = ({
    formData,
    countries,
    prevStep,
    setCurrentStep,
    setIsC6edit,
    idZadosti,
    isLegalEntity
}) => {
    const { amlz, aktivniFirma, language } = useContext(Context);
    const { navigateWorkaround } = useNavigateWorkaround();
    const [form] = Form.useForm();
    const [isC6, setIsC6] = useState<boolean>(!!amlz?.goToC6);
    const [showDownload, setShowDownload] = useState<boolean>(false);
    const [downloadSignature, setDownloadSignature] = useState<boolean>(false);
    const [showUpload, setShowUpload] = useState<boolean>(false);
    const [redirectBiSing, setRedirectBiSign] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [downloading, setDownloading] = useState<string>('');
    const [pdfChecked, setPdfChecked] = useState<boolean>(false);
    const [pdfCheckError, setPdfCheckError] = useState<string>('');
    const [osobaSoubor, setOsobaSoubor] = useState<ISubjPrilozenesoubory>();
    const [dokladyTotozFileList, setDokladyTotozFileList] = useState<IFileList[] | undefined>();
    const [deleteFile, setDeleteFile] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState<string>('');
    const c2ChybaVse = !formData.c2?.vse;
    const isPokracovatDisabled = !!formData.c2?.osoby.filter(
        e =>
            (e.stav !== EStavZadostAmlz.CDCPKEKONTR &&
                e.stav !== EStavZadostAmlz.AUTKONTROLA &&
                e.stav !== EStavZadostAmlz.CDCPKONTR &&
                e.stav !== EStavZadostAmlz.HOTOVO &&
                e.stav !== EStavZadostAmlz.HOTOVOIIS &&
                e.stav !== EStavZadostAmlz.ODEBRAT &&
                e.stav !== EStavZadostAmlz.PRIDAT) ||
            e.duplicita
    ).length;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (fieldsValue: any) => {
        const c6subjData = {
            ...formData?.c6,
            email: fieldsValue.email,
            telefon: fieldsValue.telefon
        };
        const dataToSend = {
            ...(osobaSoubor?.tempnazev && {
                prilozenysoubor: osobaSoubor
            }),
            ...(c6subjData.identifikace && {
                identifikace: c6subjData.identifikace
            }),
            email: c6subjData.email,
            jmeno: c6subjData.jmeno,
            prijmeni: c6subjData.prijmeni,
            telefon: c6subjData.telefon,
            jazyk: language
        };
        if (deleteFile) {
            setLoading(true);
            amlzC6Send(idZadosti, dataToSend, 'PUT').finally(() => setLoading(false));
            setDeleteFile(false);
        } else if (redirectBiSing) {
            // stiahnutie pdf na podpis
            amlzC6Send(idZadosti, dataToSend, 'PUT').then(() =>
                C6BisignApi(idZadosti ? idZadosti : amlz?.idzadosti || '', language)
                    .then(resp => window.location.replace(resp.redirect))
                    .finally(() => setLoading(false))
            );
        } else if (downloadSignature && !pdfChecked) {
            // stiahnutie pdf na podpis
            amlzC6Send(idZadosti, dataToSend, 'PUT').then(() =>
                C6PdfApi(idZadosti ? idZadosti : amlz?.idzadosti || '', `ZadostAMLZ_${idZadosti}.pdf`, language)
                    .then(() => {
                        setDownloading('');
                        setShowUpload(true);
                    })
                    .finally(() => {
                        setDownloadSignature(false);
                        setIsSubmitting(false);
                    })
            );
        } else if (pdfChecked) {
            // odoslanie s podpisanim pdf
            setIsSubmitting(true);
            amlzC6Send(idZadosti, dataToSend, 'POST')
                .then(resp => {
                    setOpenModal(resp.stav);
                })
                .catch(() => setIsSubmitting(false));
        } else {
            // odoslanie bez pdf
            setIsSubmitting(true);
            amlzC6Send(idZadosti, dataToSend, 'POST')
                .then(resp => {
                    setOpenModal(resp.stav);
                })
                .catch(() => setIsSubmitting(false));
        }
    };

    const uploadPropsElektPodpis = {
        showDownloadIcon: true,
        onChange: (data: UploadChangeParam) => {
            if (data.file.status === 'uploading') setDownloading('up');
            if (data.file.status !== 'uploading') setDownloading('');
            if (data.fileList)
                setDokladyTotozFileList(
                    data.fileList
                        .filter(f => f.status && f.status !== 'error')
                        .map(e => {
                            return {
                                // oid: resp.id!,
                                uid: e.uid!,
                                name: e.name.normalize()!,
                                fileName: e.fileName ? e.fileName : e.response ? e.response.tempnazev : ''!,
                                type: e.type!,
                                size: e.size!,
                                status: e.status ? e.status : 'error'
                            };
                        })
                );
            if (data.file.response && data.file.response.tempnazev) {
                const c6subjData = {
                    ...formData?.c6,
                    email: form.getFieldValue('email'),
                    telefon: form.getFieldValue('telefon')
                };
                const dataToSend = {
                    prilozenysoubor: {
                        orignazev: data.file.name,
                        tempnazev: data.file.response.tempnazev,
                        oblast: 'PODEPSANYSOUBOR'
                    },
                    ...(c6subjData.identifikace && {
                        identifikace: c6subjData.identifikace
                    }),
                    email: c6subjData.email,
                    jmeno: c6subjData.jmeno,
                    prijmeni: c6subjData.prijmeni,
                    telefon: c6subjData.telefon
                };
                amlzC6Send(idZadosti, dataToSend, 'PUT')
                    .then(resp => {
                        if (resp.stav === 'OK') {
                            setPdfChecked(true);
                            setOsobaSoubor({
                                orignazev: data.file.name,
                                tempnazev: data.file.response.tempnazev,
                                oblast: 'PODEPSANYSOUBOR'
                            });
                            setDokladyTotozFileList([
                                {
                                    uid: data.fileList[0].uid,
                                    name: data.fileList[0].name,
                                    fileName: data.fileList[0].fileName
                                        ? data.fileList[0].fileName
                                        : data.fileList[0].response
                                          ? data.fileList[0].response.tempnazev
                                          : '',
                                    type: data.fileList[0].type!,
                                    size: data.fileList[0].size!,
                                    status: data.fileList[0].status ? data.fileList[0].status : 'error'
                                }
                            ]);
                        } else {
                            setPdfCheckError('SOUBOR_CHYBA_PODPIS');
                            setDokladyTotozFileList([]);
                        }
                    })
                    .catch(() => {
                        setPdfCheckError('SOUBOR_CHYBA_PODPIS');
                        setDokladyTotozFileList([]);
                    })
                    .finally(() => setDownloading(''));
            }
        },
        onRemove: () => {
            setDeleteFile(true);
            setTimeout(() => form.submit(), 0);
            setOsobaSoubor(undefined);
            setDokladyTotozFileList([]);
        },
        onDownload: (file: UploadFile) =>
            prepareFileAmlz(idZadosti!, file.fileName ? file.fileName : '').then(resp => {
                fileDownload(resp.redirect, file.name ? file.name : 'file');
            }),
        fileList: dokladyTotozFileList,
        buttonLoading: downloading === 'up'
    };

    useEffect(() => {
        if (amlz?.goToC6) {
            setIsC6(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData?.c6]);

    useEffect(() => {
        if (downloadSignature || redirectBiSing) {
            form.validateFields()
                .then(() => onFinish(form.getFieldsValue()))
                .catch(e => {
                    setDownloadSignature(false);
                    setRedirectBiSign(false);
                    setDownloading('');
                    form.scrollToField(e.errorFields[0].name[0]);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [downloadSignature, redirectBiSing]);

    useEffect(() => {
        /*  console.log(formData.c2?.osoby, profily?.id);
        const currentUser = formData.c2?.osoby.filter(e => e.id + '' === profily?.id + '');
        if (currentUser?.length) {
            setLoading(true);
            amlzC2osobyDetailGet(idZadosti, currentUser[0].idosoby!)
                .then(resp =>
                    resp.identifikaceosoby === EZadostPravoIdentifikace.BISIGN
                        ? setShowBisign(true)
                        : resp.identifikaceosoby === EZadostPravoIdentifikace.EL
                        ? setShowDownload(true)
                        : null
                )
                .finally(() => setLoading(false));
        } */

        formData?.c6?.identifikace === EZadostPravoIdentifikace.EL ? setShowDownload(true) : null;

        form.setFieldsValue({
            osoba: formData?.c6?.jmeno + ' ' + formData?.c6?.prijmeni,
            email: formData?.c6?.email,
            telefon: formData?.c6?.telefon
        });
        setOsobaSoubor(formData?.c6?.prilozenysoubor);
        formData?.c6?.prilozenysoubor?.tempnazev &&
            setDokladyTotozFileList([
                {
                    uid: '0',
                    name: formData?.c6?.prilozenysoubor?.orignazev
                        ? formData?.c6?.prilozenysoubor.orignazev
                        : 'bez nazvu',
                    fileName: formData?.c6?.prilozenysoubor?.tempnazev ? formData?.c6?.prilozenysoubor.tempnazev : 'a',
                    type: formData?.c6?.prilozenysoubor?.orignazev
                        ? formData?.c6?.prilozenysoubor.orignazev.split('.').slice(-1)[0]
                        : '',
                    size: 0,
                    status: 'done'
                }
            ]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData?.c6, formData?.c2]);

    /* useEffect(() => {
        setDokladyTotozFileList(
            formData?.c6 && formData?.c6.prilozenysoubor
                ? [
                      {
                          uid: '0',
                          name: formData?.c6?.prilozenysoubor?.orignazev
                              ? formData?.c6?.prilozenysoubor.orignazev
                              : 'bez nazvu',
                          fileName: formData?.c6?.prilozenysoubor?.tempnazev
                              ? formData?.c6?.prilozenysoubor.tempnazev
                              : 'a',
                          type: formData?.c6?.prilozenysoubor?.orignazev
                              ? formData?.c6?.prilozenysoubor.orignazev.split('.').slice(-1)[0]
                              : '',
                          size: 0,
                          status: 'done'
                      }
                  ]
                : []
        );
        formData?.c6 &&
            formData?.c6.prilozenysoubor &&
            setOsobaSoubor({
                orignazev: formData?.c6?.prilozenysoubor.orignazev,
                tempnazev: formData?.c6?.prilozenysoubor.tempnazev,
                oblast: 'PODEPSANYSOUBOR'
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); */

    return (
        <LoadingWrapper loading={loading}>
            {formData && !isC6 && (
                <FormWrapper onFinish={() => {}} name="C6">
                    <Title text={'FORMULARE_AMLZ_C6_ZHRNUTI'} level={4} />

                    <Title text={'FORMULARE_AMLZ_C1_TITLE'} level={5} dividerTitle={true}>
                        <ButtonComponent
                            buttontext="BUTTON_UPRAVIT"
                            id="C1UpravitBtn"
                            type="primary"
                            style={{
                                marginLeft: 16
                            }}
                            onClick={() => {
                                setCurrentStep(1);
                                setIsC6edit(EAmlzFormularSteps.C1);
                            }}
                        />
                    </Title>
                    <InputComponent
                        disabled={true}
                        notRequired={true}
                        label="IDENTIFIKACNI_CISLO"
                        name="idmb"
                        placeholder={formData.c1?.idmb}
                        type="text"
                    />
                    <InputComponent
                        disabled={true}
                        notRequired={true}
                        label="ISIN_IDENTIFIKACE_FIRMA"
                        name="nazev"
                        placeholder={formData.c1?.nazev}
                        type="text"
                    />
                    <InputComponent
                        disabled={true}
                        notRequired={true}
                        label="LEI_CELE"
                        name="lei"
                        placeholder={formData.c1?.lei}
                        type="text"
                    />
                    <SelectComponent
                        isFormItem={true}
                        name="stat"
                        labelParagraph="FORMULARE_PROVOZOVNA_STATY"
                        disabled={true}
                        notRequired={true}
                        mode="multiple"
                        defaultValue={formData.c1?.stat}
                        data={countries ? countries : []}
                    />
                    <InputComponent
                        disabled={true}
                        notRequired={true}
                        label="ISIN_IDENTIFIKACE_PRAVNI_FORMA"
                        name="pravniforma"
                        placeholder={formData.c1?.pravniforma}
                        type="text"
                    />
                    <Title level={5} dividerTitle={true} text="ISIN_IDENTIFIKACE_ADRESA_SIDLA" />
                    <InputComponent
                        disabled={true}
                        notRequired={true}
                        label="ULICE_CP"
                        name="adma"
                        placeholder={formData.c1?.adma}
                        type="text"
                    />
                    <InputComponent
                        disabled={true}
                        notRequired={true}
                        label="MESTO"
                        name="mema"
                        placeholder={formData.c1?.mema}
                        type="text"
                    />
                    <InputComponent
                        disabled={true}
                        notRequired={true}
                        label="PSC"
                        name="pscm"
                        placeholder={formData.c1?.pscm}
                        type="text"
                    />
                    <SelectComponent
                        name="kost"
                        labelParagraph="STAT_SIDLA"
                        disabled={true}
                        notRequired={true}
                        isFormItem={true}
                        defaultValue={formData.c1?.kost}
                        data={countries ? countries : []}
                    />

                    <Title text={'FORMULARE_AMLZ_C2_TITLE'} level={5} dividerTitle={true}>
                        <ButtonComponent
                            buttontext="BUTTON_UPRAVIT"
                            id="C2UpravitBtn"
                            type="primary"
                            style={{
                                marginLeft: 16
                            }}
                            onClick={() => {
                                setCurrentStep(2);
                                setIsC6edit(EAmlzFormularSteps.C2);
                            }}
                        />
                    </Title>
                    <TableComponent
                        locale={{
                            emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                        }}
                        rowKey={(_, index) => index || 0}
                        style={{
                            paddingBottom: 30
                        }}
                        columns={[
                            {
                                title: <TranslateText>JMENO</TranslateText>,
                                dataIndex: 'jmeno',
                                key: 'jmeno',
                                render: (_text: ISubj, row: ISubj) => (
                                    <p
                                        style={{
                                            margin: 0
                                        }}
                                    >
                                        {row.jmeno + ' ' + row.prijmeni}
                                    </p>
                                )
                            }
                        ]}
                        dataSource={formData.c2?.osoby.filter(
                            e => e.stav !== EStavZadostAmlz.ODEBRAT && e.stav !== EStavZadostAmlz.PRIDAT
                        )}
                        loading={!formData.c2?.osoby}
                        pagination={false}
                    />

                    <Title text={'FORMULARE_AMLZ_C3A_TITLE'} level={5} dividerTitle={true}>
                        <ButtonComponent
                            buttontext="BUTTON_UPRAVIT"
                            id="C3AUpravitBtn"
                            type="primary"
                            style={{
                                marginLeft: 16
                            }}
                            onClick={() => {
                                setCurrentStep(3);
                                setIsC6edit(EAmlzFormularSteps.C3A);
                            }}
                        />
                    </Title>

                    {formData.c3prima?.ma === EAnoNe.ANO ? (
                        <TableComponent
                            locale={{
                                emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                            }}
                            rowKey={(_, index) => index || 0}
                            style={{
                                paddingBottom: 30
                            }}
                            columns={[
                                {
                                    title: <TranslateText>JMENO</TranslateText>,
                                    dataIndex: 'jmeno',
                                    key: 'jmeno',
                                    render: (_text: ISubj, row: ISubj) => (
                                        <p
                                            style={{
                                                margin: 0
                                            }}
                                        >
                                            {row.jmeno + ' ' + (row.prijmeni ? row.prijmeni : '')}
                                        </p>
                                    )
                                },
                                {
                                    title: (
                                        <>
                                            <TranslateText>FORMULARE_RODNE_CISLO</TranslateText> /{' '}
                                            <TranslateText>FORMULARE_DATUM_NAROZENI</TranslateText> /{' '}
                                            <TranslateText>ICO_ZKRATKA</TranslateText>
                                        </>
                                    ),
                                    dataIndex: 'idmb',
                                    key: 'idmb',
                                    render: (text: string, row: ISubj) => {
                                        return (
                                            <p
                                                style={{
                                                    margin: 0
                                                }}
                                            >
                                                {text ? text : dayjs(row.datumnar).format('DD.MM.YYYY')}
                                            </p>
                                        );
                                    }
                                },
                                {
                                    title: (
                                        <>
                                            <TranslateText>FORMULARE_ZEME_PUVODU</TranslateText> /{' '}
                                            <TranslateText>FORMULARE_ZEME_SIDLA</TranslateText>
                                        </>
                                    ),
                                    dataIndex: 'zemepuv',
                                    key: 'zemepuv',
                                    render: (_text: ISubj, row: ISubj) => {
                                        return (
                                            <p
                                                style={{
                                                    margin: 0
                                                }}
                                            >
                                                {row.zemepuv?.map((e, i) => (
                                                    <span
                                                        key={i}
                                                        style={{
                                                            paddingRight: 6,
                                                            display: 'inline'
                                                        }}
                                                    >
                                                        {e}
                                                    </span>
                                                ))}
                                            </p>
                                        );
                                    }
                                },
                                {
                                    title: <TranslateText>FORMULARE_PODIL</TranslateText>,
                                    dataIndex: 'podil',
                                    key: 'podil',
                                    render: (_text: ISubj, row: ISubj) => {
                                        return (
                                            <p
                                                style={{
                                                    margin: 0
                                                }}
                                            >
                                                {row.podil + '%'}
                                            </p>
                                        );
                                    }
                                }
                            ]}
                            dataSource={formData.c3prima?.subj}
                            loading={!formData.c3prima?.subj}
                            pagination={false}
                        />
                    ) : (
                        formData.c3prima?.ma && (
                            <SelectComponent
                                name="ma"
                                labelParagraph="FORMULARE_AMLZ_C3A_SELECT"
                                isFormItem={true}
                                disabled={true}
                                notRequired={true}
                                defaultValue={formData.c3prima.ma}
                                data={anoNeSelect}
                            />
                        )
                    )}

                    <Title text={'FORMULARE_AMLZ_C3B_TITLE'} level={5} dividerTitle={true}>
                        <ButtonComponent
                            buttontext="BUTTON_UPRAVIT"
                            id="C3BUpravitBtn"
                            type="primary"
                            style={{
                                marginLeft: 16
                            }}
                            onClick={() => {
                                setCurrentStep(3);
                                setIsC6edit(EAmlzFormularSteps.C3B);
                            }}
                        />
                    </Title>
                    {formData.c3neprima?.ma === EAnoNe.ANO ? (
                        <TableComponent
                            locale={{
                                emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                            }}
                            rowKey={(_, index) => index || 0}
                            style={{
                                paddingBottom: 30
                            }}
                            columns={[
                                {
                                    title: <TranslateText>JMENO</TranslateText>,
                                    dataIndex: 'jmeno',
                                    key: 'jmeno',
                                    render: (_text: ISubj, row: ISubj) => (
                                        <p
                                            style={{
                                                margin: 0
                                            }}
                                        >
                                            {row.jmeno + ' ' + (row.prijmeni ? row.prijmeni : '')}
                                        </p>
                                    )
                                },
                                {
                                    title: (
                                        <>
                                            <TranslateText>FORMULARE_RODNE_CISLO</TranslateText> /{' '}
                                            <TranslateText>FORMULARE_DATUM_NAROZENI</TranslateText> /{' '}
                                            <TranslateText>ICO_ZKRATKA</TranslateText>
                                        </>
                                    ),
                                    dataIndex: 'idmb',
                                    key: 'idmb',
                                    render: (text: string, row: ISubj) => {
                                        return (
                                            <p
                                                style={{
                                                    margin: 0
                                                }}
                                            >
                                                {text ? text : dayjs(row.datumnar).format('DD.MM.YYYY')}
                                            </p>
                                        );
                                    }
                                },
                                {
                                    title: (
                                        <>
                                            <TranslateText>FORMULARE_ZEME_PUVODU</TranslateText> /{' '}
                                            <TranslateText>FORMULARE_ZEME_SIDLA</TranslateText>
                                        </>
                                    ),
                                    dataIndex: 'zemepuv',
                                    key: 'zemepuv',
                                    render: (_text: ISubj, row: ISubj) => {
                                        return (
                                            <p
                                                style={{
                                                    margin: 0
                                                }}
                                            >
                                                {row.zemepuv?.map((e, i) => (
                                                    <span
                                                        key={i}
                                                        style={{
                                                            paddingRight: 6,
                                                            display: 'inline'
                                                        }}
                                                    >
                                                        {e}
                                                    </span>
                                                ))}
                                            </p>
                                        );
                                    }
                                },
                                {
                                    title: <TranslateText>FORMULARE_PODIL</TranslateText>,
                                    dataIndex: 'podil',
                                    key: 'podil',
                                    render: (_text: ISubj, row: ISubj) => {
                                        return (
                                            <p
                                                style={{
                                                    margin: 0
                                                }}
                                            >
                                                {row.podil + '%'}
                                            </p>
                                        );
                                    }
                                }
                            ]}
                            dataSource={formData.c3neprima.subj}
                            loading={!formData.c3neprima.subj}
                            pagination={false}
                        />
                    ) : (
                        formData.c3neprima?.ma && (
                            <SelectComponent
                                isFormItem={true}
                                name="ma"
                                labelParagraph="FORMULARE_AMLZ_C3B_SELECT"
                                disabled={true}
                                notRequired={true}
                                defaultValue={formData.c3neprima.ma}
                                data={anoNeSelect}
                            />
                        )
                    )}

                    <Title text={'FORMULARE_AMLZ_C4_TITLE'} level={5} dividerTitle={true}>
                        <ButtonComponent
                            buttontext="BUTTON_UPRAVIT"
                            id="C4UpravitBtn"
                            type="primary"
                            style={{
                                marginLeft: 16
                            }}
                            onClick={() => {
                                setCurrentStep(4);
                                setIsC6edit(EAmlzFormularSteps.C4);
                            }}
                        />
                    </Title>
                    {formData.c4?.empro === 'UV' && (
                        <>
                            <Paragraph
                                text={'FORMULARE_AMLZ_C4_RADIO_UV'}
                                style={{
                                    display: 'block'
                                }}
                            />
                            <TableComponent
                                locale={{
                                    emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                                }}
                                rowKey={(_, index) => index || 0}
                                style={{
                                    margin: '30px 0px'
                                }}
                                columns={[
                                    {
                                        title: <TranslateText>FORMULARE_SKUTECNI_MAJITELE</TranslateText>,
                                        dataIndex: 'jmeno',
                                        key: 'jmeno',
                                        render: (text: ISubj, row: ISubj) => {
                                            return (
                                                <div>
                                                    <p
                                                        style={{
                                                            margin: 0,
                                                            display: 'inline'
                                                        }}
                                                    >
                                                        {' '}
                                                        {text + ' ' + row.prijmeni}
                                                    </p>
                                                    {row.idmb && (
                                                        <p
                                                            style={{
                                                                margin: 0,
                                                                display: 'inline'
                                                            }}
                                                        >
                                                            , <TranslateText>FORMULARE_RODNE_CISLO_MALE</TranslateText>:
                                                            {' ' + row.idmb}
                                                        </p>
                                                    )}
                                                    {row.datumnar && (
                                                        <p
                                                            style={{
                                                                margin: 0,
                                                                display: 'inline'
                                                            }}
                                                        >
                                                            ,{' '}
                                                            <TranslateText>FORMULARE_DATUM_NAROZENI_MALE</TranslateText>
                                                            : {dayjs(row.datumnar).format('DD.MM.YYYY')}
                                                        </p>
                                                    )}
                                                    {row.zemepuv && row.zemepuv.length > 0 && (
                                                        <p
                                                            style={{
                                                                margin: 0,
                                                                display: 'inline'
                                                            }}
                                                        >
                                                            , <TranslateText>FORMULARE_ZEME_PUVODU_MALE</TranslateText>:
                                                            {' ' + row.zemepuv}
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        }
                                    }
                                ]}
                                dataSource={formData.c4.subj?.filter(
                                    e => e.stavesm === EEsmStav.ESMOK || e.stavesm === EEsmStav.RUCNE
                                )}
                                loading={!formData.c4.subj}
                                pagination={false}
                            />
                        </>
                    )}
                    {formData.c4?.empro === 'NEUV' && (
                        <>
                            <Paragraph
                                text={'FORMULARE_AMLZ_C4_RADIO_NEUV'}
                                style={{
                                    display: 'block'
                                }}
                            />
                            <TableComponent
                                locale={{
                                    emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                                }}
                                rowKey={(_, index) => index || 0}
                                style={{
                                    margin: '30px 0px'
                                }}
                                columns={[
                                    {
                                        title: <TranslateText>JMENO</TranslateText>,
                                        dataIndex: 'jmeno',
                                        key: 'jmeno',
                                        render: (_text: ISubj, row: ISubj) => (
                                            <p
                                                style={{
                                                    margin: 0
                                                }}
                                            >
                                                {row.jmeno + ' ' + row.prijmeni}
                                            </p>
                                        )
                                    },
                                    {
                                        title: (
                                            <>
                                                <TranslateText>FORMULARE_RODNE_CISLO</TranslateText> /{' '}
                                                <TranslateText>FORMULARE_DATUM_NAROZENI</TranslateText>
                                            </>
                                        ),
                                        dataIndex: 'idmb',
                                        key: 'idmb',
                                        render: (text: string, row: ISubj) => {
                                            return (
                                                <p
                                                    style={{
                                                        margin: 0
                                                    }}
                                                >
                                                    {text ? text : dayjs(row.datumnar).format('DD.MM.YYYY')}
                                                </p>
                                            );
                                        }
                                    }
                                ]}
                                dataSource={formData.c4.subj}
                                loading={!formData.c4.subj}
                                pagination={false}
                            />
                        </>
                    )}
                    {formData.c4?.empro === 'NENI' && (
                        <>
                            <Paragraph
                                text={'FORMULARE_AMLZ_C4_RADIO_NENI'}
                                style={{
                                    display: 'block',
                                    paddingBottom: 16
                                }}
                            />

                            <InputTextArea
                                rows={4}
                                name="duvod"
                                defaultValue={formData.c4.duvod ? formData.c4.duvod : ''}
                                label="DUVOD"
                                disabled={true}
                            />
                        </>
                    )}

                    <Title text={'FORMULARE_AMLZ_C5_TITLE'} level={5} dividerTitle={true}>
                        <ButtonComponent
                            buttontext="BUTTON_UPRAVIT"
                            id="C5UpravitBtn"
                            type="primary"
                            style={{
                                marginLeft: 16
                            }}
                            onClick={() => {
                                setCurrentStep(5);
                                setIsC6edit(EAmlzFormularSteps.C5);
                            }}
                        />
                    </Title>
                    {formData.c5?.ma === EAnoNe.ANO ? (
                        <TableComponent
                            locale={{
                                emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                            }}
                            rowKey={(_, index) => index || 0}
                            style={{
                                paddingBottom: 30
                            }}
                            columns={[
                                {
                                    title: <TranslateText>JMENO</TranslateText>,
                                    dataIndex: 'jmeno',
                                    key: 'jmeno',
                                    render: (_text: ISubj, row: ISubj) => (
                                        <p
                                            style={{
                                                margin: 0
                                            }}
                                        >
                                            {row.jmeno + ' ' + row.prijmeni}
                                        </p>
                                    )
                                },
                                {
                                    title: <TranslateText>FORMULARE_ADRESA_POBYTU</TranslateText>,
                                    dataIndex: 'adma',
                                    key: 'adma',
                                    render: (_text: ISubj, row: ISubj) => (
                                        <span>{row.adma + ', ' + row.mema + ', ' + row.pscm + ', ' + row.kost}</span>
                                    )
                                }
                            ]}
                            dataSource={formData.c5.subj}
                            loading={!formData.c5.subj}
                            pagination={false}
                        />
                    ) : (
                        formData.c5?.ma && (
                            <SelectComponent
                                name="ma"
                                isFormItem={true}
                                labelParagraph="FORMULARE_AMLZ_C5_TEXT_PROHLASENI"
                                disabled={true}
                                notRequired={true}
                                defaultValue={formData.c5.ma}
                                data={c5PepSelect}
                            />
                        )
                    )}
                    {c2ChybaVse && (
                        <Paragraph
                            style={{
                                display: 'block',
                                fontWeight: 600,
                                color: '#ff4d4f'
                            }}
                            text="AMLZ_C6_DISABLED_C2"
                        />
                    )}
                    {isPokracovatDisabled && (
                        <Paragraph
                            style={{
                                display: 'block',
                                fontWeight: 600,
                                color: '#ff4d4f'
                            }}
                            text={
                                formData.c2?.osoby.filter(e => e.duplicita).length
                                    ? 'AMLZ_C6_DISABLED_C2_DUPLICITA'
                                    : 'AMLZ_C6_DISABLED'
                            }
                        />
                    )}
                    <Row className="button-row">
                        <ButtonComponent
                            buttontext="ZPET"
                            id="C6ZpetBtn"
                            type="default"
                            onClick={() => {
                                prevStep();
                                setIsC6edit('');
                            }}
                        />
                        <ButtonComponent
                            buttontext="POKRACOVAT"
                            id="C6PokracovatBtn"
                            type="primary"
                            onClick={() => setIsC6(true)}
                            disabled={isPokracovatDisabled || c2ChybaVse}
                            loading={!!downloading}
                        />
                    </Row>
                </FormWrapper>
            )}
            {isC6 && (
                <FormWrapper onFinish={onFinish} outForm={form}>
                    <Title
                        text={isLegalEntity ? 'FORMULARE_AMLZ_C6_TITLE_LEGALENTITY' : 'FORMULARE_AMLZ_C6_TITLE'}
                        level={5}
                    />
                    <Paragraph
                        text={'FORMULARE_AMLZ_C6_TEXTA'}
                        style={{
                            display: 'block'
                        }}
                    />
                    <Paragraph
                        text={'FORMULARE_AMLZ_C6_TEXTB'}
                        style={{
                            display: 'block'
                        }}
                    />
                    <Paragraph
                        text={'FORMULARE_AMLZ_C6_TEXTC'}
                        style={{
                            display: 'block'
                        }}
                    />
                    <Paragraph
                        text={'FORMULARE_AMLZ_C6_TEXTD'}
                        style={{
                            display: 'block'
                        }}
                    />
                    <Title
                        text={isLegalEntity ? 'FORMULARE_AMLZ_C6_SUBTITLE_LEGALENTITY' : 'FORMULARE_AMLZ_C6_SUBTITLE'}
                        level={5}
                    />

                    <>
                        <InputComponent
                            label="OSOBA"
                            name="osoba"
                            type="text"
                            initialValue={formData?.c6?.jmeno + ' ' + formData?.c6?.prijmeni}
                            notRequired={true}
                            disabled={true}
                            rules={[]}
                        />
                        <InputComponent
                            label="EMAIL"
                            name="email"
                            type="text"
                            initialValue={formData?.c6?.email}
                            notRequired={true}
                            disabled={true}
                            rules={[]}
                        />
                        <InputComponent
                            label="TELEFON"
                            name="telefon"
                            type="text"
                            initialValue={formData?.c6?.telefon}
                            notRequired={true}
                            disabled={true}
                            rules={[]}
                        />
                        {showDownload && !osobaSoubor?.tempnazev && (
                            <ButtonComponent
                                id="C6PodpisDownloadBtn"
                                buttontext={'FORMULARE_STAHNOUT_KPODPISU'}
                                onClick={() => {
                                    if (showDownload) {
                                        setDownloadSignature(true);
                                        setDownloading('down');
                                    }
                                }}
                                htmlType="button"
                                style={{
                                    marginTop: 14
                                }}
                                loading={downloading === 'down'}
                            />
                        )}
                        {((showUpload && downloading !== 'down') || osobaSoubor?.tempnazev) && (
                            <>
                                <p
                                    style={{
                                        margin: 6
                                    }}
                                >
                                    <TranslateText>{'FORMULARE_PODEPSANY_SOUBOR'}</TranslateText>
                                </p>
                                <div>
                                    <UploadComponent {...uploadPropsElektPodpis} id="C6uploadBtn" />
                                </div>
                                {pdfCheckError && !pdfChecked && (
                                    <Paragraph
                                        style={{
                                            display: 'block'
                                        }}
                                        text={pdfCheckError}
                                        className="extramessage-text"
                                    />
                                )}
                            </>
                        )}
                    </>

                    <Row className="button-row">
                        <ButtonComponent
                            buttontext="ZPET"
                            onClick={() => setIsC6(false)}
                            id="C6ZpetBtn"
                            type="default"
                            disabled={!!dokladyTotozFileList?.length}
                        />
                        <ButtonComponent
                            id="C6PokracovatBtn"
                            buttontext="POKRACOVAT"
                            loading={isSubmitting && !downloading}
                            disabled={
                                (showDownload && (!showUpload || !pdfChecked)) ||
                                (formData?.c6?.identifikace === EZadostPravoIdentifikace.EL && !osobaSoubor)
                            }
                            type="primary"
                            onClick={() => form.submit()}
                        />
                    </Row>
                </FormWrapper>
            )}
            <Modal
                title={<TranslateText>DOTAZNIK_AMLZ</TranslateText>}
                open={!!openModal}
                onCancel={() => {
                    if (aktivniFirma && openModal === 'OK') {
                        navigateWorkaround(`/firma/${aktivniFirma?.iducet}${routePathEnum.PREHLED_EMITENT}`);
                    } else {
                        setOpenModal('');
                    }
                }}
                footer={
                    <Row className="button-row-modal">
                        <ButtonComponent
                            id="C6PokracovatBtn"
                            buttontext="POKRACOVAT"
                            onClick={() => {
                                if (aktivniFirma && openModal === 'OK') {
                                    navigateWorkaround(
                                        `/firma/${aktivniFirma?.iducet}${routePathEnum.PREHLED_EMITENT}`
                                    );
                                } else {
                                    setOpenModal('');
                                }
                            }}
                        />
                    </Row>
                }
            >
                <Row
                    style={{
                        paddingBottom: 16
                    }}
                >
                    <b>
                        <Paragraph text={openModal === 'OK' ? 'AMLZ_USPECH' : 'AMLZ_NEUSPECH'} />
                    </b>
                </Row>
                <Row
                    style={{
                        paddingBottom: 36
                    }}
                >
                    <Paragraph text={openModal === 'OK' ? 'AMLZ_USPECH_TEXT' : `AMLZ_NEUSPECH_${openModal}`} />
                </Row>
            </Modal>
        </LoadingWrapper>
    );
};

export default C6;
